import { Autocomplete, TextField } from '@mui/material';
import React, { SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FilterActionName, useFilterAction, useFilterState, useSystemPropertyFilterStringValue } from '../../state/FilterContext';
import { useFilterStateController } from '../../state/filter-state-controller.context';
import { useSiteContext } from '../../state/SiteContext';
import { FilterState } from '../../state/state-models';
import { applyDefaults, getStateAsLocationQuery } from '../../state/state-url-management';
import { useToggleSwitchController } from '../../state/toggle-switch-controller.context';
import { buildFilterRedirectNavigateTo, hasFilterRedirectNavigateIntent } from '../../shared/utils/filter-redirect-url.utils';
import { TextExtractor, findFirstComponentOfType } from '../internal/TextExtractor';
import { activateToggleSwitchByName } from './toggle-switch-activation.utils';

type SetterState = "validity" | "baseline" | "custom";

interface StateSetterInputProps {
  /** "validity", "baseline", or "custom". Anything else renders an inert field that never commits. */
  state?: string,
  /** Custom property name. A trimmed non-empty value wins over `<arg-property>`. */
  property?: string,
  label?: string,
  redirect?: string,
  redirectLayout?: string,
  activateToggleSwitch?: string,
  autocompleteProps?: string,
  /** Lower-case spelling as it arrives from uploaded markup (not in the parser attributeCaseMap). */
  autocompleteprops?: string,
  textfieldProps?: string,
  /** Lower-case spelling as it arrives from uploaded markup (not in the parser attributeCaseMap). */
  textfieldprops?: string,
  children?: React.ReactNode,
}

/**
 * Parses a JSON props attribute. Malformed JSON warns and falls back to `{}` instead of
 * throwing during render.
 */
function parseJsonPropsAttribute(raw: string | undefined, attributeName: string): Record<string, any> {
  const trimmed = raw?.trim();
  if (!trimmed) {
    return {};
  }
  try {
    const parsed = JSON.parse(trimmed);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      console.warn(`StateSetterInput: ${attributeName} must be a JSON object; ignoring the value.`);
      return {};
    }
    return parsed as Record<string, any>;
  }
  catch (error) {
    console.warn(`StateSetterInput: Could not parse ${attributeName} as JSON; ignoring the value.`, error);
    return {};
  }
}

/**
 * Sets one custom property on a cloned filter-state bag and clears that key's default marker.
 * Other custom keys are left untouched. Always persist as a one-element string array.
 */
function applyCustomPropertyWrite(target: FilterState, name: string, typed: string): void {
  target.customProperties = { ...(target.customProperties ?? {}), [name]: [typed] };
  const nextDefaults = { ...(target.defaultCustomProps ?? {}) };
  delete nextDefaults[name];
  target.defaultCustomProps = nextDefaults;
}

/**
 * Removes one custom property and its default marker. Other keys stay. Does not insert a
 * site default; callers must not run `applyDefaults` on this path.
 */
function applyCustomPropertyClear(target: FilterState, name: string): void {
  const nextCustom = { ...(target.customProperties ?? {}) };
  delete nextCustom[name];
  target.customProperties = nextCustom;
  if (target.defaultCustomProps && Object.prototype.hasOwnProperty.call(target.defaultCustomProps, name)) {
    const nextDefaults = { ...target.defaultCustomProps };
    delete nextDefaults[name];
    target.defaultCustomProps = nextDefaults;
  }
}

/***
 * A free-text input that *sets* validity, baseline, or one custom property independently of the
 * value currently in that field. Validity and baseline compose an authored `arg-validity` prefix
 * plus the typed text. Custom writes `customProperties[name] = [trimmedText]` for the name from
 * the `property` attribute or `<arg-property>`, and clears `defaultCustomProps[name]`.
 *
 * The committed value replaces the whole field (or that custom key only); it is never merged with
 * the current value and never validated against a valueset. An empty or whitespace-only commit
 * removes that target and does nothing else: no prefix, no redirect, no toggle, and no `hid`
 * removal. A non-empty commit still waits while a configured prefix or custom name is unresolved,
 * and the typed text is kept until that commit can proceed. Custom with no usable name never commits.
 *
 * See `docs/features/state-setter-input.md`.
 */
export function StateSetterInput({
  state,
  property,
  label,
  redirect,
  redirectLayout = "",
  activateToggleSwitch,
  autocompleteProps,
  autocompleteprops,
  textfieldProps,
  textfieldprops,
  children,
}: StateSetterInputProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { siteState } = useSiteContext();
  const filterState = useFilterState();
  const updateValidity = useFilterAction(FilterActionName.UPDATE_VALIDITY);
  const updateBaseline = useFilterAction(FilterActionName.UPDATE_BASELINE);
  const setFilterState = useFilterAction(FilterActionName.SET_STATE);
  const removeSystemProperty = useFilterAction(FilterActionName.REMOVE_SYSTEM_PROPERTY);
  const hidFilter = useSystemPropertyFilterStringValue("hid");
  const controller = useFilterStateController();
  const toggleSwitchController = useToggleSwitchController();

  const usedState: SetterState | null =
    state === "validity" || state === "baseline" || state === "custom" ? state : null;
  const isCustom = usedState === "custom";

  const invalidStateWarnedForRef = useRef<string | null>(null);
  useEffect(() => {
    if (usedState) {
      return;
    }
    const warnedFor = state ?? "";
    if (invalidStateWarnedForRef.current === warnedFor) {
      return;
    }
    invalidStateWarnedForRef.current = warnedFor;
    console.warn(`StateSetterInput: Invalid or missing state "${warnedFor}". Expected "validity", "baseline", or "custom"; no state change will be made.`);
  }, [state, usedState]);

  const autocompletePropsObject = useMemo(
    () => parseJsonPropsAttribute(autocompleteProps ?? autocompleteprops, "autocompleteProps"),
    [autocompleteProps, autocompleteprops],
  );
  const textfieldPropsObject = useMemo(
    () => parseJsonPropsAttribute(textfieldProps ?? textfieldprops, "textfieldProps"),
    [textfieldProps, textfieldprops],
  );

  // Get the label from the label child
  const labelChild = useMemo(() => findFirstComponentOfType(children, "arg-label"), [children]);
  const [labelValue, setLabelValue] = useState(label || "");

  // Custom name: trimmed non-empty `property` wins and does not wait on the child.
  const attributeName = isCustom ? (property ?? "").trim() : "";
  const propertyChild = useMemo(() => {
    if (!isCustom || attributeName) {
      return null;
    }
    return findFirstComponentOfType(children, "arg-property");
  }, [children, isCustom, attributeName]);
  const hasPropertySlot = !!propertyChild;
  const [childNameText, setChildNameText] = useState<string | null>(null);
  const childName = (childNameText ?? "").trim();
  const resolvedName = attributeName || childName;
  const nameReady = !!attributeName || !hasPropertySlot || !!childName;
  const hasUsableName = !!resolvedName && nameReady;

  const missingNameWarnedRef = useRef(false);
  useEffect(() => {
    if (!isCustom || hasUsableName || hasPropertySlot) {
      return;
    }
    if (missingNameWarnedRef.current) {
      return;
    }
    missingNameWarnedRef.current = true;
    console.warn("StateSetterInput: No usable custom property name. Set a non-empty property attribute or an arg-property child; no state change will be made.");
  }, [isCustom, hasUsableName, hasPropertySlot]);

  // Get the parent prefix from the validity child. The slot is known synchronously from the
  // children, while its text resolves asynchronously through TextExtractor. Custom ignores it.
  const prefixChild = useMemo(() => {
    if (usedState !== "validity" && usedState !== "baseline") {
      return null;
    }
    return findFirstComponentOfType(children, "arg-validity");
  }, [children, usedState]);
  const hasPrefixSlot = !!prefixChild;
  const [prefixText, setPrefixText] = useState<string | null>(null);
  const prefix = (prefixText ?? "").trim();
  // A configured slot with empty text counts as unresolved: TextExtractor reports "" both before
  // its content resolves and when the content is genuinely empty. Authors omit the slot to mean
  // "no prefix".
  const prefixReady = !hasPrefixSlot || !!prefix;

  const currentValue = useMemo(() => {
    if (!usedState) {
      return "";
    }
    const sourceState = controller?.effectiveState ?? filterState;
    if (usedState === "custom") {
      if (!hasUsableName) {
        return "";
      }
      const values = sourceState.customProperties?.[resolvedName];
      if (!values?.length) {
        return "";
      }
      return String(values[0]);
    }
    return (usedState === "baseline" ? sourceState.baseline : sourceState.validity) ?? "";
  }, [usedState, controller?.effectiveState, filterState, hasUsableName, resolvedName]);

  const displayValue = useMemo(() => {
    if (usedState === "custom" || !usedState) {
      return currentValue;
    }
    if (!hasPrefixSlot) {
      return currentValue;
    }
    if (!prefixReady) {
      return "";
    }
    const prefixWithSeparator = `${prefix}:`;
    return currentValue.startsWith(prefixWithSeparator)
      ? currentValue.substring(prefixWithSeparator.length)
      : "";
  }, [usedState, hasPrefixSlot, prefixReady, prefix, currentValue]);

  // The input is controlled through inputValue (not value) so that a blocked commit,
  // a late prefix/name resolution, or an unrelated filter change never overwrites the typed text.
  // After a clear, displayValue becomes empty and this effect keeps the field empty once focus leaves.
  const [inputValue, setInputValue] = useState(displayValue);
  const isFocusedRef = useRef(false);
  useEffect(() => {
    if (!isFocusedRef.current) {
      setInputValue(displayValue);
    }
  }, [displayValue]);

  const unresolvedPrefixWarnedRef = useRef(false);
  const unresolvedNameWarnedRef = useRef(false);
  const redirectWarningLoggedRef = useRef(false);

  const commit = useCallback((rawText: string | null) => {
    if (!usedState) {
      return;
    }
    const typed = (rawText ?? "").trim();
    if (usedState === "custom") {
      if (!hasUsableName) {
        if (hasPropertySlot && !nameReady && !unresolvedNameWarnedRef.current) {
          unresolvedNameWarnedRef.current = true;
          console.warn("StateSetterInput: The arg-property name is not resolved yet, so nothing was committed. Omit the arg-property slot when the property attribute supplies the name.");
        }
        return;
      }
    }
    else if (typed && hasPrefixSlot && !prefixReady) {
      if (!unresolvedPrefixWarnedRef.current) {
        unresolvedPrefixWarnedRef.current = true;
        console.warn('StateSetterInput: The arg-validity parent prefix is not resolved yet, so nothing was committed. Omit the arg-validity slot when the typed text is the whole value.');
      }
      return;
    }
    if (!typed) {
      const customName = usedState === "custom" ? resolvedName : "";
      if (controller) {
        controller.requestChange((stateSnapshot) => {
          const nextState: FilterState = { ...stateSnapshot };
          if (usedState === "custom") {
            applyCustomPropertyClear(nextState, customName);
          }
          else if (usedState === "baseline") {
            nextState.baseline = "";
          }
          else {
            nextState.validity = "";
          }
          return nextState;
        }, { source: `statesetterinput:${usedState}` });
        return;
      }
      if (usedState === "custom") {
        const nextState: FilterState = { ...filterState };
        applyCustomPropertyClear(nextState, customName);
        setFilterState(nextState);
        return;
      }
      if (usedState === "baseline") {
        updateBaseline("");
        return;
      }
      updateValidity("");
      return;
    }
    const nextValue = usedState === "custom" || !prefix ? typed : `${prefix}:${typed}`;
    const customName = usedState === "custom" ? resolvedName : "";

    if (controller) {
      if (hasFilterRedirectNavigateIntent(redirect, redirectLayout) && !redirectWarningLoggedRef.current) {
        console.warn('[FilterStateController] Ignoring redirect and redirectLayout behavior for "StateSetterInput" in controller scope.');
        redirectWarningLoggedRef.current = true;
      }

      controller.requestChange((stateSnapshot) => {
        const nextState: FilterState = { ...stateSnapshot };
        if (nextState.systemProperties?.["hid"]) {
          const nextSystemProps = { ...nextState.systemProperties };
          delete nextSystemProps["hid"];
          nextState.systemProperties = nextSystemProps;
        }
        if (usedState === "custom") {
          applyCustomPropertyWrite(nextState, customName, nextValue);
        }
        else if (usedState === "baseline") {
          nextState.baseline = nextValue;
        }
        else {
          nextState.validity = nextValue;
        }
        return nextState;
      }, { source: `statesetterinput:${usedState}` });
      activateToggleSwitchByName(activateToggleSwitch, toggleSwitchController, "StateSetterInput");
      return;
    }

    let navigateTo: string | null = null;
    if (hasFilterRedirectNavigateIntent(redirect, redirectLayout)) {
      // Merged filter state is deep-frozen in development, so clone every property bag that
      // applyDefaults or the hid removal below writes into.
      const filterStateCopy: FilterState = {
        ...filterState,
        systemProperties: { ...(filterState.systemProperties ?? {}) },
        customProperties: { ...(filterState.customProperties ?? {}) },
        defaultCustomProps: { ...(filterState.defaultCustomProps ?? {}) },
      };
      if (hidFilter) {
        delete filterStateCopy.systemProperties?.["hid"];
      }
      if (usedState === "custom") {
        applyCustomPropertyWrite(filterStateCopy, customName, nextValue);
      }
      else if (usedState === "baseline") {
        filterStateCopy.baseline = nextValue;
      }
      else {
        filterStateCopy.validity = nextValue;
      }
      if (siteState?.site) {
        applyDefaults(filterStateCopy, siteState.site);
        if (usedState === "validity") {
          // applyDefaults rebuilds validity from the segments it can resolve in Site.validities,
          // which truncates a free-text value such as "serial-number:1234567890" to
          // "serial-number". Re-assert the committed value; the call is still needed for the
          // default language and default custom properties it adds to the redirect URL.
          filterStateCopy.validity = nextValue;
        }
        else if (usedState === "custom") {
          // applyDefaults fills a custom key only when it is missing, but it also writes the
          // default marker. Re-assert the user value and cleared marker so a typed string that
          // equals the site default still serializes as c.<name>.
          applyCustomPropertyWrite(filterStateCopy, customName, nextValue);
        }
      }
      const searchParams = getStateAsLocationQuery(filterStateCopy);
      navigateTo = buildFilterRedirectNavigateTo({
        effectiveSitePath: siteState.sitePath ?? "",
        redirect,
        redirectLayout,
        currentPathname: location.pathname,
        searchParamsString: searchParams.toString(),
        locationHash: location.hash,
        activeToggles: activateToggleSwitch,
      });
    }

    if (usedState === "custom") {
      // SET_CUSTOM_PROPERTY does not clear defaultCustomProps[name], so one SET_STATE writes
      // the value, the cleared marker, and the hid drop together.
      const nextState: FilterState = {
        ...filterState,
        customProperties: { ...(filterState.customProperties ?? {}) },
        defaultCustomProps: { ...(filterState.defaultCustomProps ?? {}) },
        systemProperties: { ...(filterState.systemProperties ?? {}) },
      };
      applyCustomPropertyWrite(nextState, customName, nextValue);
      if (hidFilter) {
        delete nextState.systemProperties?.["hid"];
      }
      setFilterState(nextState);
    }
    else if (usedState === "baseline") {
      updateBaseline(nextValue);
    }
    else {
      updateValidity(nextValue);
    }
    // A free-text write is a new scope for the visitor, so a help id filter must not narrow it.
    // Custom drops hid inside the SET_STATE payload above.
    if (usedState !== "custom" && hidFilter) {
      removeSystemProperty("hid");
    }
    activateToggleSwitchByName(activateToggleSwitch, toggleSwitchController, "StateSetterInput");
    if (navigateTo) {
      navigate(navigateTo, { replace: true });
    }
  }, [
    usedState,
    hasUsableName,
    hasPropertySlot,
    nameReady,
    resolvedName,
    hasPrefixSlot,
    prefixReady,
    prefix,
    controller,
    redirect,
    redirectLayout,
    activateToggleSwitch,
    toggleSwitchController,
    filterState,
    hidFilter,
    siteState.site,
    siteState.sitePath,
    location.pathname,
    location.hash,
    setFilterState,
    updateValidity,
    updateBaseline,
    removeSystemProperty,
    navigate,
  ]);

  // Enter commits explicitly so that re-submitting unchanged text still redirects. MUI runs its own
  // key handling after ours and fires onChange for the same key press; suppress that duplicate
  // until the event dispatch has finished.
  const enterCommitInProgressRef = useRef(false);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key !== "Enter") {
      return;
    }
    enterCommitInProgressRef.current = true;
    queueMicrotask(() => {
      enterCommitInProgressRef.current = false;
    });
    commit(inputValue);
  };

  const resolvedTextfieldProps = useMemo(
    () => ({ ...textfieldPropsObject, label: labelValue || textfieldPropsObject.label }),
    [textfieldPropsObject, labelValue],
  );

  return <>
    {labelChild && (
      <TextExtractor onExtractedText={setLabelValue}>
        {labelChild.props.children}
      </TextExtractor>
    )}
    {propertyChild && (
      <TextExtractor onExtractedText={setChildNameText}>
        {propertyChild.props.children}
      </TextExtractor>
    )}
    {prefixChild && (
      <TextExtractor onExtractedText={setPrefixText}>
        {prefixChild.props.children}
      </TextExtractor>
    )}
    <Autocomplete freeSolo {...autocompletePropsObject}
      options={[]}
      inputValue={inputValue}
      onInputChange={(event: SyntheticEvent<Element, Event>, newInputValue: string, reason: string) => {
        // "reset" is MUI rewriting the field from its own internal value; ignoring it is what keeps
        // the typed text in place when a commit is blocked.
        if (reason === "input" || reason === "clear") {
          setInputValue(newInputValue);
        }
      }}
      onKeyDown={handleKeyDown}
      onChange={(event: SyntheticEvent<Element, Event>, newValue: string | null) => {
        if (enterCommitInProgressRef.current) {
          return;
        }
        commit(newValue);
      }}
      onFocus={() => {
        isFocusedRef.current = true;
      }}
      onBlur={() => {
        isFocusedRef.current = false;
        if (inputValue !== displayValue) {
          commit(inputValue);
        }
      }}
      renderInput={(params: any) => {
        return <TextField {...params} {...resolvedTextfieldProps} />
      }}
    />
  </>;
}
